/**
 * WeatherWidget — Module B du dashboard
 * Météo étendue avec gradient adaptatif et phase lunaire
 */
import React, { memo, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Wind, Droplets, Gauge, Sun, Sunrise, Sunset, CloudRain } from 'lucide-react';
import weatherService from '../../services/weatherService';

const WEATHER_BG = {
  clear:  'linear-gradient(160deg, rgba(180,83,9,0.22) 0%, rgba(14,14,20,1) 70%)',
  cloudy: 'linear-gradient(160deg, rgba(71,85,105,0.22) 0%, rgba(14,14,20,1) 70%)',
  rain:   'linear-gradient(160deg, rgba(37,99,235,0.22) 0%, rgba(14,14,20,1) 70%)',
  snow:   'linear-gradient(160deg, rgba(148,163,184,0.18) 0%, rgba(14,14,20,1) 70%)',
  fog:    'linear-gradient(160deg, rgba(100,116,139,0.18) 0%, rgba(14,14,20,1) 70%)',
  storm:  'linear-gradient(160deg, rgba(109,40,217,0.25) 0%, rgba(14,14,20,1) 70%)',
};

function OutdoorBadge({ score }) {
  if (score === true)
    return <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
      style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399', border: '1px solid rgba(16,185,129,0.2)' }}>
      ✓ Idéal
    </span>;
  if (score === false)
    return <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
      style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)' }}>
      ✗ Rester
    </span>;
  return <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
    style={{ background: 'rgba(245,158,11,0.15)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.2)' }}>
    ~ Mitigé
  </span>;
}

function UVLabel({ uv }) {
  if (!uv && uv !== 0) return null;
  const color = uv <= 2 ? '#34d399' : uv <= 5 ? '#facc15' : uv <= 7 ? '#fb923c' : '#f87171';
  return <span style={{ color, fontWeight: 700 }}>{uv}</span>;
}

export const WeatherWidget = memo(function WeatherWidget({ weather, moon, isLoading, error }) {
  const [expanded, setExpanded] = useState(false);
  const outdoorScore = useMemo(() => weatherService.getOutdoorScore(weather), [weather]);
  const bgGradient = WEATHER_BG[weather?.current?.category] || WEATHER_BG.cloudy;

  const cardBase = {
    background: bgGradient,
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '1rem',
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center px-4 py-3 rounded-2xl"
        style={{ background: 'rgba(14,14,20,0.8)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex flex-col items-center gap-1">
          <span className="text-2xl animate-pulse">🌤️</span>
          <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.25)' }}>Météo…</span>
        </div>
      </div>
    );
  }

  if (error || !weather?.current) {
    return (
      <div className="flex-1 flex items-center justify-center px-4 py-3 rounded-2xl"
        style={{ background: 'rgba(14,14,20,0.8)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <span className="text-sm" style={{ color: 'rgba(255,255,255,0.25)' }}>❓ Météo indisponible</span>
      </div>
    );
  }

  const { current, today } = weather;

  return (
    <motion.button
      className="flex-1 text-left px-3 py-3 overflow-hidden relative"
      style={cardBase}
      onClick={() => setExpanded(v => !v)}
      aria-label={`Météo : ${current.description}. Tap pour plus de détails.`}
      aria-expanded={expanded}
    >
      {/* Ligne principale */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span className="text-3xl leading-none">{current.icon}</span>
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-[26px] font-bold font-mono leading-none" style={{ color: 'rgba(240,240,252,0.9)' }}>
                {current.temperature}°
              </span>
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>/ {current.feelsLike}°</span>
            </div>
            <p className="text-[10px] mt-0.5 leading-tight" style={{ color: 'rgba(255,255,255,0.38)' }}>
              {current.description}
            </p>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5">
          <OutdoorBadge score={outdoorScore} />
          {moon && (
            <span className="text-[10px] font-mono" style={{ color: 'rgba(255,255,255,0.28)' }}>
              {moon.icon} {moon.illumination}%
            </span>
          )}
          <span style={{ color: 'rgba(255,255,255,0.2)' }}>
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </span>
        </div>
      </div>

      {/* Infos inline */}
      <div className="flex items-center gap-3 mt-1.5">
        <span className="text-[9px] flex items-center gap-0.5" style={{ color: 'rgba(255,255,255,0.22)' }}>
          <Droplets className="w-2.5 h-2.5" />{current.humidity}%
        </span>
        {current.windSpeed && (
          <span className="text-[9px] flex items-center gap-0.5" style={{ color: 'rgba(255,255,255,0.22)' }}>
            <Wind className="w-2.5 h-2.5" />{current.windSpeed} km/h
          </span>
        )}
      </div>

      {/* Panneau étendu */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden"
          >
            <div className="mt-2 pt-2 grid grid-cols-2 gap-x-3 gap-y-1.5"
              style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              {current.pressure && (
                <WeatherRow icon={<Gauge className="w-3 h-3" />} label="Pression" value={`${current.pressure} hPa`} />
              )}
              <WeatherRow icon={<Wind className="w-3 h-3" />} label="Vent"
                value={`${current.windSpeed} km/h ${current.windDirectionLabel || ''}`} />
              {today?.uvIndex != null && (
                <div className="flex items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.28)' }}>
                  <Sun className="w-3 h-3 flex-shrink-0" />
                  <span className="text-[10px]">UV <UVLabel uv={today.uvIndex} /></span>
                </div>
              )}
              {today?.precipitation != null && (
                <WeatherRow icon={<CloudRain className="w-3 h-3" />} label="Pluie"
                  value={`${today.precipitation.toFixed(1)} mm`} />
              )}
              {today?.sunrise && (
                <WeatherRow icon={<Sunrise className="w-3 h-3" />} label="Lever" value={today.sunrise} />
              )}
              {today?.sunset && (
                <WeatherRow icon={<Sunset className="w-3 h-3" />} label="Coucher" value={today.sunset} />
              )}
              {today?.tempMax != null && (
                <WeatherRow icon={<span className="text-[10px]">🌡</span>} label="Min/Max"
                  value={`${today.tempMin}° / ${today.tempMax}°`} />
              )}
              {moon && (
                <div className="col-span-2 flex items-center gap-1.5 pt-1.5 mt-0.5"
                  style={{ borderTop: '1px solid rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.25)' }}>
                  <span className="text-base leading-none">{moon.icon}</span>
                  <span className="text-[10px]">{moon.phase} — {moon.illumination}% éclairée</span>
                  {moon.daysToFullMoon > 0 && (
                    <span className="text-[9px] ml-auto" style={{ color: 'rgba(255,255,255,0.18)' }}>
                      pleine dans {moon.daysToFullMoon}j
                    </span>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
});

const WeatherRow = memo(function WeatherRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.28)' }}>
      <span className="flex-shrink-0">{icon}</span>
      <span className="text-[10px]">
        {label} <span style={{ color: 'rgba(255,255,255,0.6)' }}>{value}</span>
      </span>
    </div>
  );
});

export default WeatherWidget;
